import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import {
  criarSolicitacaoEnvio,
  listarDestinosMesmoContainer,
  enviarNotaDiretamente,
} from "../utils/solicitacoesEnvio";
import "./EnviarNotaModal.css";

export default function EnviarNotaModal({
  nota,
  containerOrigemId,
  usuarioId,
  entityAtualTipo,   // "project" | "setor" — de onde a nota está sendo enviada (para excluir da lista de destinos no mesmo container)
  entityAtualId,
  onNotaEnviada,      // (info) => void — chamado só no envio direto (mesmo container), depois de mover/copiar com sucesso
  onClose,
}) {
  // step: "modo" | "containers" | "entidades" | "pilhas" | "confirmar"
  const [step, setStep] = useState("modo");
  const [modoEnvio, setModoEnvio] = useState(null); // "mesmo" | "outro"
  const [loading, setLoading] = useState(false);
  const [containers, setContainers] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [pilhas, setPilhas] = useState([]);
  const [containerSelecionado, setContainerSelecionado] = useState(null);
  const [entidadeSelecionada, setEntidadeSelecionada] = useState(null);
  const [pilhaSelecionada, setPilhaSelecionada] = useState(null);
  const [enviando, setEnviando] = useState(false);

  // ── Passo inicial: escolher entre "neste container" e "outro container" ──
  const escolherMesmoContainer = async () => {
    setModoEnvio("mesmo");
    setStep("entidades");
    setLoading(true);
    try {
      const lista = await listarDestinosMesmoContainer({
        usuarioId,
        containerId: containerOrigemId,
        entidadeAtualTipo: entityAtualTipo,
        entidadeAtualId: entityAtualId,
      });
      setEntidades(lista);
    } catch (err) {
      console.error("Erro ao carregar destinos do container:", err);
      setEntidades([]);
    } finally {
      setLoading(false);
    }
  };

  const escolherOutroContainer = () => {
    setModoEnvio("outro");
    setStep("containers");
  };

  useEffect(() => {
    if (modoEnvio !== "outro" || step !== "containers") return;
    const carregarContainers = async () => {
      setLoading(true);
      const { data: permissoes } = await supabase
        .from("permissoes_colaboradores")
        .select("container_id")
        .eq("colaborador_id", usuarioId);
      const idsUnicos = [...new Set((permissoes || []).map(p => p.container_id).filter(Boolean))];
      if (idsUnicos.length === 0) { setContainers([]); setLoading(false); return; }
      const { data: perfis } = await supabase.from("profiles").select("id, nickname, nome, avatar_url").in("id", idsUnicos);
      setContainers(perfis || []);
      setLoading(false);
    };
    carregarContainers();
  }, [modoEnvio, step, usuarioId]);

  const selecionarContainer = async (container) => {
    setContainerSelecionado(container);
    setStep("entidades");
    setLoading(true);
    const { data: permissoes } = await supabase
      .from("permissoes_colaboradores")
      .select("projeto_id, setor_id")
      .eq("colaborador_id", usuarioId)
      .eq("container_id", container.id);

    const projetoIds = [...new Set((permissoes || []).map(p => p.projeto_id).filter(Boolean))];
    const setorIds = [...new Set((permissoes || []).map(p => p.setor_id).filter(Boolean))];

    const [{ data: projetos }, { data: setores }] = await Promise.all([
      projetoIds.length ? supabase.from("projects").select("id, name, nickname").in("id", projetoIds) : Promise.resolve({ data: [] }),
      setorIds.length ? supabase.from("setores").select("id, name, nickname").in("id", setorIds) : Promise.resolve({ data: [] }),
    ]);

    const lista = [
      ...(projetos || []).map(p => ({ ...p, tipo: "project" })),
      ...(setores || []).map(s => ({ ...s, tipo: "setor" })),
    ];
    setEntidades(lista);
    setLoading(false);
  };

  const selecionarEntidade = async (entidade) => {
    setEntidadeSelecionada(entidade);
    setStep("pilhas");
    setLoading(true);
    const campo = entidade.tipo === "project" ? "project_id" : "setor_id";
    const { data: pilhasRaw } = await supabase
      .from("pilhas")
      .select("*")
      .eq(campo, entidade.id)
      .or("arquivada.is.null,arquivada.eq.false")
      .order("ordem", { ascending: true });
    setPilhas((pilhasRaw || []).filter(p => p.tipo_pilha !== "diario_obras" && p.tipo_pilha !== "sistema_calendario"));
    setLoading(false);
  };

  const selecionarPilha = (pilha) => { setPilhaSelecionada(pilha); setStep("confirmar"); };

  // ── Confirma envio via solicitação (cross-container, precisa de aprovação) ──
  const confirmarEnvioComAprovacao = async (tipoOperacao) => {
    setEnviando(true);
    try {
      await criarSolicitacaoEnvio({
        nota,
        usuarioId,
        containerOrigemId,
        containerDestinoId: containerSelecionado.id,
        projetoDestinoId: entidadeSelecionada.tipo === "project" ? entidadeSelecionada.id : null,
        setorDestinoId: entidadeSelecionada.tipo === "setor" ? entidadeSelecionada.id : null,
        pilhaDestino: pilhaSelecionada,
        tipoOperacao,
      });
      alert("Solicitação enviada! Aguardando aprovação do responsável pelo container.");
      onClose();
    } catch (err) {
      alert(`Erro ao enviar solicitação: ${err.message || "erro desconhecido"}`);
    } finally {
      setEnviando(false);
    }
  };

  // ── Confirma envio direto (mesmo container, sem aprovação) ──
  const confirmarEnvioDireto = async (tipoOperacao) => {
    setEnviando(true);
    try {
      const resultado = await enviarNotaDiretamente({ nota, pilhaDestino: pilhaSelecionada, tipoOperacao });
      onNotaEnviada?.(resultado);
      onClose();
    } catch (err) {
      alert(`Erro ao enviar nota: ${err.message || "erro desconhecido"}`);
    } finally {
      setEnviando(false);
    }
  };

  const confirmarEnvio = (tipoOperacao) => {
    if (modoEnvio === "mesmo") return confirmarEnvioDireto(tipoOperacao);
    return confirmarEnvioComAprovacao(tipoOperacao);
  };

  const voltar = () => {
    if (step === "containers") { setStep("modo"); setModoEnvio(null); }
    else if (step === "entidades") {
      if (modoEnvio === "mesmo") { setStep("modo"); setModoEnvio(null); setEntidades([]); }
      else { setStep("containers"); setContainerSelecionado(null); }
    }
    else if (step === "pilhas") { setStep("entidades"); setEntidadeSelecionada(null); }
    else if (step === "confirmar") { setStep("pilhas"); setPilhaSelecionada(null); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content enviar-nota-modal">
        <div className="enviar-nota-modal__header">
          {step !== "modo" && (
            <button className="enviar-nota-modal__back" onClick={voltar}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          )}
          <h3>Enviar nota</h3>
          <button className="enviar-nota-modal__close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <p className="enviar-nota-modal__subtitle">"{nota.nome}"</p>

        {loading ? (
          <div className="enviar-nota-modal__loading">Carregando...</div>
        ) : step === "modo" ? (
          <div className="enviar-nota-modal__list">
            <button className="enviar-nota-modal__item" onClick={escolherMesmoContainer}>
              <span className="material-symbols-outlined">swap_horiz</span>
              <span>Para outro projeto/setor deste container</span>
            </button>
            <button className="enviar-nota-modal__item" onClick={escolherOutroContainer}>
              <span className="material-symbols-outlined">send</span>
              <span>Para outro container</span>
            </button>
          </div>
        ) : step === "containers" ? (
          <div className="enviar-nota-modal__list">
            {containers.length === 0 && <p className="enviar-nota-modal__empty">Você não colabora em outros containers.</p>}
            {containers.map(c => (
              <button key={c.id} className="enviar-nota-modal__item" onClick={() => selecionarContainer(c)}>
                {c.avatar_url ? <img src={c.avatar_url} alt="" /> : <div className="enviar-nota-modal__avatar-fallback">{(c.nickname || c.nome || "?")[0].toUpperCase()}</div>}
                <span>{c.nickname || c.nome}</span>
              </button>
            ))}
          </div>
        ) : step === "entidades" ? (
          <div className="enviar-nota-modal__list">
            {entidades.length === 0 && (
              <p className="enviar-nota-modal__empty">
                {modoEnvio === "mesmo" ? "Nenhum outro projeto ou setor disponível para você neste container." : "Nenhum projeto ou setor disponível."}
              </p>
            )}
            {entidades.map(e => (
              <button key={`${e.tipo}-${e.id}`} className="enviar-nota-modal__item" onClick={() => selecionarEntidade(e)}>
                <span className="material-symbols-outlined">{e.tipo === "project" ? "folder" : "apartment"}</span>
                <span>{e.nickname || e.name}</span>
              </button>
            ))}
          </div>
        ) : step === "pilhas" ? (
          <div className="enviar-nota-modal__list">
            {pilhas.length === 0 && <p className="enviar-nota-modal__empty">Nenhuma pilha disponível.</p>}
            {pilhas.map(p => (
              <button key={p.id} className="enviar-nota-modal__item" onClick={() => selecionarPilha(p)}>
                <span className="material-symbols-outlined">view_column</span>
                <span>{p.title}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="enviar-nota-modal__confirmar">
            <p>Enviar para a pilha <strong>{pilhaSelecionada.title}</strong> em <strong>{entidadeSelecionada.nickname || entidadeSelecionada.name}</strong>.</p>
            {modoEnvio === "mesmo" ? (
              <p className="enviar-nota-modal__aviso" style={{ color: "#16a34a" }}>
                Envio direto — não precisa de aprovação, a nota é movida/copiada imediatamente.
              </p>
            ) : (
              <p className="enviar-nota-modal__aviso">O responsável pelo container precisa aprovar antes que a nota seja movida/copiada.</p>
            )}
            <div className="enviar-nota-modal__acoes">
              <button disabled={enviando} onClick={() => confirmarEnvio("mover")}>Mover</button>
              <button disabled={enviando} onClick={() => confirmarEnvio("copiar")}>Copiar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}